import { assert, fail } from '@datapad/utilities';
import { daysPerYear, holidays, monthsAndFestivalWeeks, monthsAndFestivalWeeksPerYear, weekdays, } from '../Constants.js';
import { doesMonthContainDay, getMonthFromDay } from '../Utilities.js';
import { schemaFactory } from './Factory.js';
export class CalendarDate extends schemaFactory.object('calendar-date', {
    year: schemaFactory.number,
    day: schemaFactory.number,
}) {
    static create(year, day) {
        assert(Number.isInteger(day), `Expected an integer day, received: ${day}`);
        if (day < 0 || day >= daysPerYear) {
            throw new Error(`Provided day is outside the allowed range. Expected on [0, ${daysPerYear - 1}], but was: ${day}`);
        }
        return new CalendarDate({
            year,
            day,
        });
    }
    static fromBBY(yearBBY, day) {
        return new CalendarDate({
            year: 3577 - yearBBY,
            day,
        });
    }
    toLongString() {
        const dayOfTheMonth = this.getDayOfTheMonth();
        return `${dayOfTheMonth.name}, ${this.getMonth().name}/${dayOfTheMonth.dayOfTheMonth + 1}/${this.year}`;
    }
    toShortString() {
        return `${this.getMonth().shorthand}/${this.getDayOfTheMonth().dayOfTheMonth + 1}/${this.year}`;
    }
    equals(other) {
        return this.year === other.year && this.day === other.day;
    }
    compareTo(other) {
        const yearCompare = this.year - other.year;
        if (yearCompare !== 0) {
            return yearCompare;
        }
        return this.day - other.day;
    }
    getMonth() {
        for (let i = 0; i < monthsAndFestivalWeeksPerYear; i++) {
            const currentMonthOrFestivalWeek = monthsAndFestivalWeeks[i];
            if (doesMonthContainDay(this.day, currentMonthOrFestivalWeek)) {
                return {
                    ...currentMonthOrFestivalWeek,
                    year: this.year,
                };
            }
        }
        throw new Error(`Specified day "${this.day}" is out of year range. This should have been caught above.`);
    }
    get isHoliday() {
        return holidays.some((holiday) => holiday.dayOfTheYear === this.day);
    }
    getDayOfTheMonth() {
        const monthOrFestivalWeek = getMonthFromDay(this.day);
        if (monthOrFestivalWeek.kind === 'festival-week') {
            const dayOfTheWeek = this.day - monthOrFestivalWeek.startDay;
            const weekdayName = weekdays[dayOfTheWeek % 5];
            return {
                name: weekdayName,
                dayOfTheMonth: dayOfTheWeek,
                isHoliday: false,
            };
        }
        const dayOfTheMonth = this.day - monthOrFestivalWeek.startDay;
        if (this.isHoliday) {
            const name = holidays.find((holiday) => holiday.dayOfTheYear === this.day)?.name ??
                fail(`Holiday not found for day ${this.day}`);
            return {
                name,
                dayOfTheMonth,
                isHoliday: true,
            };
        }
        return {
            name: weekdays[dayOfTheMonth % 5],
            dayOfTheMonth,
            isHoliday: false,
        };
    }
    dateFromOffset(offset) {
        assert(Number.isInteger(offset) && Number.isFinite(offset));
        let year = this.year;
        const dayIndex = this.day + offset;
        if (dayIndex < 0) {
            year--;
        }
        else if (dayIndex > daysPerYear) {
            year++;
        }
        const day = dayIndex % daysPerYear;
        return new CalendarDate({
            year,
            day,
        });
    }
    static monthUrlKey = 'month';
    toUrlSearch() {
        const month = this.getMonth();
        const nameForUrl = month.name.replace(/ /g, '-');
        return `${CalendarDate.monthUrlKey}=${month.year}-${nameForUrl}`;
    }
    static parseUrlSearch(searchParams) {
        const monthParam = searchParams.get(CalendarDate.monthUrlKey);
        if (monthParam === null) {
            return undefined;
        }
        const dashIndex = monthParam.indexOf('-');
        if (dashIndex === -1) {
            return undefined;
        }
        const yearStr = monthParam.substring(0, dashIndex);
        const nameWithDashes = monthParam.substring(dashIndex + 1);
        const year = parseInt(yearStr, 10);
        if (isNaN(year)) {
            return undefined;
        }
        const nameToMatch = nameWithDashes.replace(/-/g, ' ').toLowerCase();
        const monthOrFestival = monthsAndFestivalWeeks.find((m) => m.name.toLowerCase() === nameToMatch);
        if (monthOrFestival === undefined) {
            return undefined;
        }
        return new CalendarDate({ year, day: monthOrFestival.startDay });
    }
}
//# sourceMappingURL=CalendarDate.js.map